import {
  fetchGogoMakes,
  fetchGogoModelYears,
  fetchGogoModels,
  fetchGogoTrims,
} from "@/lib/gogoMasterDataApi";
import { fetchPetrominCities, type PetrominCity } from "@/lib/petrominCitiesApi";

export type GogoMake = {
  Make?: string | null;
  MakeId: number;
  MakeKey?: string | null;
  BrandSlug?: string | null;
  IsActive?: boolean;
};

export type GogoModelYear = {
  ModelYearId: number;
  ModelYear: string;
  ModelYearCode?: number;
};

export type GogoModelRow = {
  Model?: string | null;
  ModelId: number;
  ModelKey?: string | null;
  MakeId: number;
  IsActive?: boolean;
};

/** Trim / variant row from `models/{modelId}/trims` (maps to `variant_id` on vehicles/manage). */
export type GogoTrim = {
  Trim?: string | null;
  TrimId: number;
  TrimCode?: number;
  TrimSlug?: string | null;
  FuelType?: string | null;
  Transmission?: string | null;
  IsActive?: boolean;
};

type GogoListResponse<T> = {
  success?: boolean;
  data?: T[];
};

function stripUuidSuffix(key: string): string {
  const idx = key.lastIndexOf("_");
  if (idx <= 0) return key;
  const tail = key.slice(idx + 1);
  if (/^[0-9a-f-]{36}$/i.test(tail) || /^[0-9a-f-]{8}-/i.test(tail)) {
    return key.slice(0, idx).replace(/_/g, " ").trim() || key;
  }
  return key.replace(/_/g, " ");
}

export function makeDisplayName(m: GogoMake): string {
  const direct = (m.Make ?? "").trim();
  if (direct) return direct;
  const slug = (m.BrandSlug ?? "").trim();
  if (slug) return slug;
  const key = (m.MakeKey ?? "").trim();
  if (!key) return `Make #${m.MakeId}`;
  return stripUuidSuffix(key);
}

export function modelDisplayName(m: GogoModelRow): string {
  const name = (m.Model ?? "").trim();
  if (name) return name;
  const key = (m.ModelKey ?? "").trim();
  if (!key) return `Model #${m.ModelId}`;
  return stripUuidSuffix(key);
}

export function trimDisplayName(t: GogoTrim): string {
  const name = (t.Trim ?? "").trim() || (t.TrimSlug ?? "").trim();
  if (name) return name;
  const id = t.TrimId ?? t.TrimCode;
  return id != null ? `Trim #${id}` : "Trim";
}

export function localeToGogoLanguageId(locale: string): number {
  return locale === "ar" ? 2 : 1;
}

export type GogoCityRecord = {
  /** Petromin CS city UUID (legacy GoGo API used numeric ids). */
  CityId: number | string;
  CityKey?: string | null;
  City?: string | null;
  CityEn?: string | null;
  CityAr?: string | null;
  Latitude?: string | null;
  Longitude?: string | null;
  ImageUrl?: string | null;
  ImageUrlPath?: string | null;
};

function petrominCityToRecord(
  city: PetrominCity,
  languageId: number
): GogoCityRecord {
  const nameEn = city.name;
  const nameAr = city.native?.trim() || nameEn;
  const display = languageId === 2 ? nameAr : nameEn;
  return {
    CityId: city.id,
    CityKey: city.id,
    City: display,
    CityEn: nameEn,
    CityAr: nameAr,
    Latitude: city.latitude ?? null,
    Longitude: city.longitude ?? null,
  };
}

export async function fetchCitiesClient(
  languageId: number,
  opts?: { page?: number; limit?: number; searchKey?: string }
): Promise<{ records: GogoCityRecord[]; totalRecords: number }> {
  void opts?.page;
  void opts?.limit;
  const sk = opts?.searchKey?.trim();
  try {
    const cities = await fetchPetrominCities(sk || undefined);
    const records = cities.map((c) => petrominCityToRecord(c, languageId));
    return { records, totalRecords: records.length };
  } catch {
    return { records: [], totalRecords: 0 };
  }
}

export async function fetchMakesClient(languageId: number): Promise<GogoMake[]> {
  try {
    const json = (await fetchGogoMakes(languageId)) as GogoListResponse<GogoMake>;
    if (!Array.isArray(json.data)) return [];
    return json.data.filter((row) => row.IsActive !== false);
  } catch {
    return [];
  }
}

export async function fetchModelYearsClient(): Promise<GogoModelYear[]> {
  try {
    const json = (await fetchGogoModelYears()) as GogoListResponse<GogoModelYear>;
    if (!Array.isArray(json.data)) return [];
    return json.data;
  } catch {
    return [];
  }
}

export async function fetchModelsClient(
  makeId: number,
  modelYearId: number,
  languageId: number
): Promise<GogoModelRow[]> {
  try {
    const json = (await fetchGogoModels(
      makeId,
      modelYearId,
      languageId
    )) as GogoListResponse<GogoModelRow>;
    if (!Array.isArray(json.data)) return [];
    return json.data.filter((row) => row.IsActive !== false);
  } catch {
    return [];
  }
}

export async function fetchTrimsClient(
  modelId: number,
  languageId: number
): Promise<GogoTrim[]> {
  try {
    const json = (await fetchGogoTrims(
      modelId,
      languageId
    )) as GogoListResponse<GogoTrim>;
    if (!Array.isArray(json.data)) return [];
    return json.data.filter((row) => row.IsActive !== false);
  } catch {
    return [];
  }
}
